import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useToast } from '../components/ui/toast';
import { meetingAPI } from '../services/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { DatePicker } from '../components/ui/date-picker';
import { TimePicker } from '../components/ui/time-picker';
import {
  Video,
  Plus,
  Users,
  ChevronLeft,
  ChevronRight,
  Trash2,
  FileVideo,
  MoreHorizontal,
  Loader2,
  Calendar,
  Clock
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../components/ui/dropdown-menu';

type MeetingType = 'instant' | 'scheduled';
type WeekDay = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

const WEEK_DAYS: { value: WeekDay; label: string }[] = [
  { value: 'sunday', label: 'Sun' },
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
];

interface Meeting {
  id: string;
  title: string;
  description?: string;
  type: MeetingType;
  status: string;
  start_date?: string | null;
  start_time?: string | null;
  end_date?: string | null;
  end_time?: string | null;
  available_days?: WeekDay[] | null;
  created_at: string;
  participant_count?: number; // For backward compatibility - represents current sessions
  total_participants?: number; // Total participants from meeting_participants table
  current_sessions?: number; // Active sessions with status 'joined'
}

const Dashboard: React.FC = () => {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [meetingToDelete, setMeetingToDelete] = useState<Meeting | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingDescription, setMeetingDescription] = useState('');
  const [meetingType, setMeetingType] = useState<MeetingType>('instant');
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [startTime, setStartTime] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [endTime, setEndTime] = useState<string | undefined>(undefined);
  const [availableDays, setAvailableDays] = useState<WeekDay[]>([]);
  const [isRecordingEnabled, setIsRecordingEnabled] = useState(false);
  const [isScreenSharingEnabled, setIsScreenSharingEnabled] = useState(true);
  const [isChatEnabled, setIsChatEnabled] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const { addToast } = useToast();
  const navigate = useNavigate();

  const fetchMeetings = useCallback(async () => {
    try {
      const response = await meetingAPI.list();
      setMeetings(response.data.data.meetings || []);
    } catch (error) {
      console.error('Failed to fetch meetings:', error);
      addToast({
        title: "Error",
        description: "Failed to load meetings. Please refresh the page.",
        variant: "destructive"
      });
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  const handleCreateMeeting = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate required title before submission
    if (!meetingTitle.trim()) {
      addToast({
        title: "Title required",
        description: "Please enter a meeting title before creating the meeting.",
        variant: "destructive",
      });
      return;
    }

    // Validate scheduled meeting has at least start or end datetime
    if (meetingType === 'scheduled') {
      const hasStart = startDate || startTime;
      const hasEnd = endDate || endTime;
      if (!hasStart && !hasEnd) {
        addToast({
          title: "Schedule required",
          description: "Scheduled meetings require at least start or end date/time.",
          variant: "destructive",
        });
        return;
      }
    }

    setCreateLoading(true);

    try {
      const payload: any = {
        title: meetingTitle,
        description: meetingDescription,
        type: meetingType,
        is_recording_enabled: isRecordingEnabled,
        is_screen_sharing_enabled: isScreenSharingEnabled,
        is_chat_enabled: isChatEnabled,
      };

      // Add scheduling fields only for scheduled meetings
      if (meetingType === 'scheduled') {
        if (startDate) payload.start_date = format(startDate, 'yyyy-MM-dd');
        if (startTime) payload.start_time = startTime;
        if (endDate) payload.end_date = format(endDate, 'yyyy-MM-dd');
        if (endTime) payload.end_time = endTime;
        if (availableDays.length > 0) payload.available_days = availableDays;
      }

      const response = await meetingAPI.create(payload);

      const newMeeting = response.data.data.meeting;
      setMeetings([newMeeting, ...meetings]);
      resetForm();
      setShowCreateDialog(false);

      addToast({
        title: "Meeting Created",
        description: `Meeting has been created successfully.`,
        variant: "success"
      });
    } catch (error) {
      console.error('Failed to create meeting:', error);
      addToast({
        title: "Creation Failed",
        description: "Failed to create meeting. Please try again.",
        variant: "destructive"
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const resetForm = () => {
    setMeetingTitle('');
    setMeetingDescription('');
    setMeetingType('instant');
    setStartDate(undefined);
    setStartTime(undefined);
    setEndDate(undefined);
    setEndTime(undefined);
    setAvailableDays([]);
    setIsRecordingEnabled(false);
    setIsScreenSharingEnabled(true);
    setIsChatEnabled(true);
  };

  const toggleDay = (day: WeekDay) => {
    setAvailableDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };




  const handleDeleteMeeting = async () => {
    if (!meetingToDelete) return;

    setDeleteLoading(true);
    try {
      await meetingAPI.delete(meetingToDelete.id);
      setMeetings(meetings.filter(m => m.id !== meetingToDelete.id));
      setShowDeleteDialog(false);
      setMeetingToDelete(null);

      addToast({
        title: "Meeting Deleted",
        description: "Meeting has been deleted successfully.",
        variant: "success"
      });
    } catch (error) {
      console.error('Failed to delete meeting:', error);
      addToast({
        title: "Delete Failed",
        description: "Failed to delete meeting. Please try again.",
        variant: "destructive"
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteDialog = (meeting: Meeting) => {
    setMeetingToDelete(meeting);
    setShowDeleteDialog(true);
  };



  // Pagination logic
  const totalPages = Math.ceil(meetings.length / itemsPerPage);
  const paginatedMeetings = meetings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  return (
    <Layout
      title="Meetings"
      description="Manage your meetings and view activity"
    >
      <TooltipProvider>
        <div className="mobile-padding py-4 sm:py-6 lg:py-8 space-y-4 sm:space-y-6">
          {/* Create Meeting Action */}
          <div className="flex justify-end items-center">
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                onClick={() => setShowCreateDialog(true)}
              >
                <Plus className="h-4 w-4" />
                Create Meeting
              </DialogTrigger>
              <DialogContent className="sm:max-w-[900px] max-h-[85vh] overflow-hidden p-0">
                <form onSubmit={handleCreateMeeting} className="flex flex-col max-h-[85vh]">
                  <div className="px-6 pt-6 pb-4 flex-shrink-0">
                    <DialogHeader className="space-y-3">
                      <DialogTitle>Create New Meeting</DialogTitle>
                      <DialogDescription>
                        Set up a new meeting room with optional details
                      </DialogDescription>
                    </DialogHeader>
                  </div>

                  <div className="flex-1 overflow-y-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-4">
                      {/* Left Column - Basic Info */}
                      <div className="space-y-6">
                        {/* Title Input */}
                        <div className="space-y-3">
                          <Label htmlFor="title" className="text-sm font-medium">Title</Label>
                          <Input
                            id="title"
                            placeholder="Meeting title"
                            value={meetingTitle}
                            onChange={(e) => setMeetingTitle(e.target.value)}
                            required
                            aria-invalid={!meetingTitle.trim()}
                            className="w-full"
                          />
                          <p className="text-xs text-muted-foreground">Title is required.</p>
                        </div>

                        {/* Description Input */}
                        <div className="space-y-3">
                          <Label htmlFor="description" className="text-sm font-medium">Description (optional)</Label>
                          <Textarea
                            id="description"
                            placeholder="Meeting description"
                            value={meetingDescription}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMeetingDescription(e.target.value)}
                            rows={3}
                            className="w-full resize-none"
                          />
                        </div>

                        {/* Meeting Type Selection */}
                        <div className="space-y-3">
                          <Label className="text-sm font-medium">Meeting Type</Label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant={meetingType === 'instant' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setMeetingType('instant')}
                              className="flex-1"
                            >
                              <Video className="h-4 w-4 ltr:mr-2 rtl:ml-2" />
                              Instant
                            </Button>
                            <Button
                              type="button"
                              variant={meetingType === 'scheduled' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setMeetingType('scheduled')}
                              className="flex-1"
                            >
                              <Calendar className="h-4 w-4 ltr:mr-2 rtl:ml-2" />
                              Scheduled
                            </Button>
                          </div>
                        </div>

                        {/* Scheduling Fields - Only show for scheduled meetings */}
                        {meetingType === 'scheduled' && (
                          <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                            <div className="text-sm font-medium flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              Schedule Settings
                            </div>

                            {/* Start Date/Time */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-2">
                                <Label className="text-xs">Start Date</Label>
                                <DatePicker
                                  date={startDate}
                                  onDateChange={setStartDate}
                                  placeholder="Select date"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs">Start Time</Label>
                                <TimePicker
                                  time={startTime}
                                  onTimeChange={setStartTime}
                                  placeholder="Select time"
                                />
                              </div>
                            </div>

                            {/* End Date/Time */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-2">
                                <Label className="text-xs">End Date</Label>
                                <DatePicker
                                  date={endDate}
                                  onDateChange={setEndDate}
                                  placeholder="Select date"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs">End Time</Label>
                                <TimePicker
                                  time={endTime}
                                  onTimeChange={setEndTime}
                                  placeholder="Select time"
                                />
                              </div>
                            </div>

                            {/* Available Days */}
                            <div className="space-y-2">
                              <Label className="text-xs">Available Days (optional)</Label>
                              <div className="flex flex-wrap gap-2">
                                {WEEK_DAYS.map((day) => (
                                  <button
                                    key={day.value}
                                    type="button"
                                    onClick={() => toggleDay(day.value)}
                                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                                      availableDays.includes(day.value)
                                        ? 'bg-primary text-primary-foreground border-primary'
                                        : 'bg-background hover:bg-muted border-input'
                                    }`}
                                  >
                                    {day.label}
                                  </button>
                                ))}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Leave empty for all days
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Right Column - Settings */}
                      <div className="space-y-5 md:border-l md:pl-6">
                        <div className="text-sm font-semibold">Meeting Settings</div>

                        {/* Recording Toggle */}
                        <div className="flex items-start justify-between gap-4 py-2">
                          <div className="space-y-1 flex-1">
                            <Label htmlFor="recording-enabled" className="text-sm font-medium cursor-pointer">
                              Enable Recording
                            </Label>
                            <div className="text-sm text-muted-foreground leading-relaxed">
                              Allow meeting host to record sessions
                            </div>
                          </div>
                          <Switch
                            id="recording-enabled"
                            checked={isRecordingEnabled}
                            onCheckedChange={setIsRecordingEnabled}
                            className="mt-1 flex-shrink-0"
                          />
                        </div>

                        {/* Screen Sharing Toggle */}
                        <div className="flex items-start justify-between gap-4 py-2">
                          <div className="space-y-1 flex-1">
                            <Label htmlFor="screen-sharing-enabled" className="text-sm font-medium cursor-pointer">
                              Enable Screen Sharing
                            </Label>
                            <div className="text-sm text-muted-foreground leading-relaxed">
                              Allow participants to share their screens
                            </div>
                          </div>
                          <Switch
                            id="screen-sharing-enabled"
                            checked={isScreenSharingEnabled}
                            onCheckedChange={setIsScreenSharingEnabled}
                            className="mt-1 flex-shrink-0"
                          />
                        </div>

                        {/* Chat Toggle */}
                        <div className="flex items-start justify-between gap-4 py-2">
                          <div className="space-y-1 flex-1">
                            <Label htmlFor="chat-enabled" className="text-sm font-medium cursor-pointer">
                              Enable Chat
                            </Label>
                            <div className="text-sm text-muted-foreground leading-relaxed">
                              Allow participants to send messages
                            </div>
                          </div>
                          <Switch
                            id="chat-enabled"
                            checked={isChatEnabled}
                            onCheckedChange={setIsChatEnabled}
                            className="mt-1 flex-shrink-0"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 pb-6 pt-4 border-t flex-shrink-0">
                    <DialogFooter className="gap-2">
                      <Button type="button" variant="outline" onClick={() => { resetForm(); setShowCreateDialog(false); }}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={createLoading || !meetingTitle.trim()}>
                        {createLoading ? 'Creating...' : 'Create Meeting'}
                      </Button>
                    </DialogFooter>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Delete Meeting Confirmation Dialog */}
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-hidden p-0">
              <div className="flex flex-col max-h-[85vh]">
                <div className="px-6 pt-6 pb-6 flex-shrink-0">
                  <DialogHeader className="space-y-4">
                    <DialogTitle>Delete Meeting</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to delete this meeting? This action cannot be undone.
                      All participants will be removed and the meeting link will no longer work.
                    </DialogDescription>
                  </DialogHeader>
                </div>

                <div className="flex-1 overflow-y-auto px-6 pb-6">
                  {meetingToDelete && (
                    <div className="bg-muted p-5 rounded-lg space-y-4">
                      <h4 className="font-medium text-base">{meetingToDelete.title}</h4>
                      {meetingToDelete.description && (
                        <p className="text-sm text-muted-foreground">{meetingToDelete.description}</p>
                      )}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Users className="h-4 w-4" />
                            </TooltipTrigger>
                            <TooltipContent>
                              Total Participants
                            </TooltipContent>
                          </Tooltip>
                          {meetingToDelete.total_participants || 0} participants
                        </span>
                        <span className="flex items-center gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="h-2 w-2 rounded-full bg-green-500" />
                            </TooltipTrigger>
                            <TooltipContent>
                              Currently Active
                            </TooltipContent>
                          </Tooltip>
                          {meetingToDelete.current_sessions || 0} active
                        </span>
                        <Badge variant={meetingToDelete.status === 'active' ? 'default' : 'secondary'}>
                          {meetingToDelete.status}
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>

                <div className="px-6 pb-6 pt-5 border-t flex-shrink-0">
                  <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
                    <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleteLoading} className="w-full sm:w-auto">
                      Cancel
                    </Button>
                    <Button variant="destructive" onClick={handleDeleteMeeting} disabled={deleteLoading} className="w-full sm:w-auto">
                      {deleteLoading ? 'Deleting...' : 'Delete Meeting'}
                    </Button>
                  </DialogFooter>
                </div>
              </div>
            </DialogContent>
          </Dialog>



          {/* Meetings Table */}
          <Card>

            <CardContent className="p-0">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                  <p className="text-sm text-muted-foreground">Loading meetings...</p>
                </div>
              ) : meetings.length === 0 ? (
                <div className="text-center py-12">
                  <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Video className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">No meetings found</h3>
                  <p className="text-muted-foreground mb-4">
                    Create your first meeting to get started
                  </p>
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Meeting
                  </Button>
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="hidden md:block rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Participants</TableHead>
                          <TableHead>Active Sessions</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedMeetings.map((meeting) => (
                          <TableRow key={meeting.id}>
                            <TableCell className="font-medium">
                              {meeting.title}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {meeting.type === 'scheduled' ? (
                                  <><Calendar className="h-3 w-3 ltr:mr-1 rtl:ml-1 me-1" /> Scheduled</>
                                ) : (
                                  <><Video className="h-3 w-3 ltr:mr-1 rtl:ml-1 me-1" />  Instant</>
                                )}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={meeting.status === 'active' ? 'default' : 'secondary'}
                              >
                                {meeting.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Users className="h-4 w-4" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Total Participants
                                  </TooltipContent>
                                </Tooltip>
                                {meeting.total_participants || 0}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="h-2 w-2 rounded-full bg-green-500" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Currently Active
                                  </TooltipContent>
                                </Tooltip>
                                {meeting.current_sessions || 0}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDate(meeting.created_at)}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => navigate(`/dashboard/meetings/${meeting.id}/participants`)}
                                    >
                                      <Users className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Manage Participants
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => navigate(`/dashboard/meetings/${meeting.id}/recordings`)}
                                    >
                                      <FileVideo className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    View Recordings
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => openDeleteDialog(meeting)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Delete Meeting
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="md:hidden divide-y divide-border">
                    {paginatedMeetings.map((meeting) => (
                      <div key={meeting.id} className="p-4 space-y-3">
                        {/* Header with Title and Actions */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-base truncate">{meeting.title}</h3>
                            {meeting.description && (
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                {meeting.description}
                              </p>
                            )}
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-9 w-9 p-0 touch-target flex-shrink-0"
                              >
                                <MoreHorizontal className="h-5 w-5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => navigate(`/dashboard/meetings/${meeting.id}/participants`)}
                              >
                                <Users className="h-4 w-4 mr-2" />
                                Manage Participants
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => navigate(`/dashboard/meetings/${meeting.id}/recordings`)}
                              >
                                <FileVideo className="h-4 w-4 mr-2" />
                                View Recordings
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => openDeleteDialog(meeting)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Meeting
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>

                        {/* Status and Stats */}
                        <div className="flex items-center gap-3 flex-wrap">
                          <Badge variant="outline" className="capitalize">
                            {meeting.type === 'scheduled' ? (
                              <><Calendar className="h-3 w-3 ltr:mr-1 rtl:ml-1" /> Scheduled</>
                            ) : (
                              <><Video className="h-3 w-3 ltr:mr-1 rtl:ml-1" /> Instant</>
                            )}
                          </Badge>
                          <Badge
                            variant={meeting.status === 'active' ? 'default' : 'secondary'}
                          >
                            {meeting.status}
                          </Badge>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Users className="h-4 w-4" />
                            <span>{meeting.total_participants || 0} participants</span>
                          </div>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <div className="h-2 w-2 rounded-full bg-green-500" />
                            <span>{meeting.current_sessions || 0} active</span>
                          </div>
                        </div>

                        {/* Created Date */}
                        <div className="text-xs text-muted-foreground">
                          Created {formatDate(meeting.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-4 border-t">
                      <div className="text-sm text-muted-foreground">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, meetings.length)} of {meetings.length} entries
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="touch-target"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          <span className="hidden sm:inline ml-1">Previous</span>
                        </Button>
                        <div className="flex items-center space-x-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <Button
                              key={page}
                              variant={currentPage === page ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(page)}
                              className="w-9 h-9 p-0 touch-target"
                            >
                              {page}
                            </Button>
                          ))}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className="touch-target"
                        >
                          <span className="hidden sm:inline mr-1">Next</span>
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </TooltipProvider>
    </Layout>
  );
};

export default Dashboard;
